import React, {useEffect, useState} from "react";
import StateType from "../../store/store";
import {connect, ConnectedProps} from "react-redux";
import useAccessToken from "../../hooks/useAccessToken";
import {BrokerNotPickedUpAlert, BrokerUserDTO, NewNotification, UserProfile} from "../../types";
import {useNavigate} from "react-router-dom";
import AuthAPI from "../../api/AuthAPI";
import NotificationActions from "../../store/actions/NotificationActions";
import moment from "moment-timezone";
import {useInterval} from "usehooks-ts";
import {BrandingElement} from "../../BrandingTypes";

const map_state_to_props = (state: StateType) => ({
    state: {
        is_broker: state.account.is_broker,
        last_account_action: state.account.last_account_action,
        session_expires_in_seconds: state.account.session_expires_in_seconds,
    }
});

const map_dispatch_to_props = (dispatch: any) => ({
    actions: {
        push_notification: (notification: NewNotification) => dispatch(NotificationActions.push_notification(notification))
    }
});

const connector = connect(map_state_to_props, map_dispatch_to_props);

type Props = ConnectedProps<typeof connector> & {
    children: React.ReactNode;
};

const AccountTimeoutContainer = ({children, actions, state}: Props) => {

    const [loading, set_loading] = useState<boolean>(false);
    const [has_checked_password_expiration, set_has_checked_password_expiration] = useState<boolean>(false);
    const [has_checked_not_picked_up_alert, set_has_checked_not_picked_up_alert] = useState<boolean>(false);
    const [has_checked_broker_alert_timeout, set_has_checked_broker_alert_timeout] = useState<boolean>(false);
    const access_token = useAccessToken();
    const navigate = useNavigate();

    const check_password_expiration = (me: UserProfile) => {
        if (has_checked_password_expiration) return;
        const password_expiration_utc = moment.utc(me.passwordExpirationUTC);
        const now = moment.utc();
        const time_until_expiration = moment.duration(password_expiration_utc.diff(now));
        if (time_until_expiration.asDays() <= 15) {
            const rounded_days = Math.ceil(time_until_expiration.asDays());
            actions.push_notification({
                title: 'Your Password is Expiring Soon',
                action: state.is_broker ? '/broker/change_password' : '/admin/settings',
                count: 0,
                sub_title: `Your password will expire in ${rounded_days} day${rounded_days !== 1 ? 's' : ''}. We recommend changing it soon.`
            })
        }
        set_has_checked_password_expiration(true);
    };

    const get_broker_carrier = (me: BrokerUserDTO, carrier_guid: string) => (
        me.brokerData.carriers.find(carrier => carrier.carrier.guid.trim().toLowerCase() === carrier_guid.trim().toLowerCase())
    );


    const user_is_broker = (me: UserProfile) => {
        return new Set<string>((me.roles ?? []).map(role => role.toLowerCase().trim())).has('broker');
    };


    const load_notifications = async () => {
        if (loading) return;
        set_loading(true);
        let me: UserProfile;
        try {
            me = await AuthAPI.me(access_token);
        } catch (e) {
            navigate('/logout');
            return;
        }
        check_password_expiration(me);
        set_loading(false);
    };

    useEffect(() => {
        load_notifications().then();
    }, []);

    useInterval(() => {
        load_notifications().then();
    }, loading ? null : 25 * 1000);

    useInterval(() => {
        const last_account_action = moment(state.last_account_action);
        if (last_account_action.isValid()) {
            const seconds_since_last_action = moment().diff(last_account_action, 'seconds');
            const session_expires_in_seconds = state.session_expires_in_seconds;
            if (seconds_since_last_action > session_expires_in_seconds) {
                navigate('/logout');
                return;
            }
        }
    }, 1000)

    return (
        <React.Suspense fallback={<></>}>
            {children}
        </React.Suspense>
    );
};

export default connector(AccountTimeoutContainer);
